'use client';

import { c as _c } from "react/compiler-runtime";
import React, { PropsWithChildren, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { DateField } from '@sola/ark-core/molecules/date-field/date-field.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { PhoneField } from '@sola/ark-core/molecules/phone-field/phone-field.tsx';
import { PostalCodeField } from '@sola/ark-core/molecules/postal-code-field/postal-code-field.tsx';
import { Select, SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { StateField } from '@sola/ark-core/molecules/state-field/state-field.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { CountryCode, StateEnum } from '@sola/schema/dotcom/shared/geo.ts';
import { AdditionalSettingsFormDynamic } from '@sola/schema/lib/schema.ts';
import { LeadEventRequest } from '@sola/schema/lib/skippy.ts';
import { LeadFormChurchCityMaxLength, LeadFormFirstNameMaxLength, LeadFormLastNameMaxLength } from '@sola/skippy-sdk/constants.ts';
import { trackLeadFormSuccess } from '@sola/tracking/components/datalayer.tsx';
import { camelCaseToLabel } from '@sola/utils/string.ts';
import { AssetLinksFooter } from '../asset-links.tsx';
export type EventFormProps = {
  onSubmit: (data: LeadEventRequest) => Promise<boolean>;
  config?: Partial<AdditionalSettingsFormDynamic>;
};
const EventFormState = z.object({
  isSubmitting: z.boolean().optional(),
  errorMessage: z.string().optional(),
  showThankYou: z.boolean().optional()
});
type EventFormState = z.infer<typeof EventFormState>;
const FormQueryState = z.object({
  isPastor: z.boolean().optional(),
  isChildHost: z.boolean().optional(),
  hasPermissionToHost: z.boolean().optional(),
  acknowledged: z.boolean().optional(),
  consent: z.boolean().optional()
});
type FormQueryState = z.infer<typeof FormQueryState>;
const AddressFieldsProps = z.object({
  fieldGroupPrefix: z.string().optional(),
  autoCompletePrefix: z.string().optional()
});
type AddressFieldsProps = z.infer<typeof AddressFieldsProps>;
const FormColumnProps = z.object({
  className: z.string().optional()
});
type FormColumnProps = z.infer<typeof FormColumnProps> & PropsWithChildren;
type MaterialItemsProps = {
  config?: Partial<AdditionalSettingsFormDynamic>;
};
type ItemProps = {
  id?: string;
  pmId?: string;
  kitId?: string;
  label?: string;
};
export const stateOptions = Object.entries(StateEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr
}));
const makeOption = (value: string) => {
  return {
    text: camelCaseToLabel(value),
    value
  };
};
export const FormColumn: React.FC<FormColumnProps> = ({
  className,
  children
}) => <div className={cn('grid sm:grid-cols-2 gap-comfy-sm', {
  [`${className}`]: !!className
})}>
    {children}
  </div>;
const MaterialItems: React.FC<MaterialItemsProps> = t0 => {
  const $ = _c(10);
  const {
    config
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["forms"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const orderGroups = config?.materialOrder;
  const getItems = _temp2;
  if (!orderGroups || orderGroups.length === 0) {
    return null;
  }
  let t2;
  if ($[1] !== config?.materialOrderHeading || $[2] !== t) {
    t2 = config?.materialOrderHeading ?? t("forms:materialOrder.header");
    $[1] = config?.materialOrderHeading;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== orderGroups || $[5] !== t) {
    t3 = orderGroups?.map((group, index) => <Select label={t(group.label ?? `Group ${index}`)} name={`items-${index}`} items={getItems(group.options ?? [])} placeholder={t("forms:materialOrder.placeholder")} />);
    $[4] = orderGroups;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <Fieldset header={t2}>{t3}</Fieldset>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const AddressFields: React.FC<AddressFieldsProps> = t0 => {
  const $ = _c(36);
  const {
    fieldGroupPrefix,
    autoCompletePrefix
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["core", "forms", "skippy"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const getTranslationKey = _temp3;
  const isChurchFields = fieldGroupPrefix === "church";
  const formattedAutoCompletePrefix = autoCompletePrefix ? `${autoCompletePrefix} ` : "";
  let t2;
  if ($[1] !== isChurchFields || $[2] !== t) {
    t2 = isChurchFields ? t("skippy:form.compassionSunday.churchAddressLabel") : t(getTranslationKey("addressLabel"));
    $[1] = isChurchFields;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `${fieldGroupPrefix}Address`;
  const t4 = `${formattedAutoCompletePrefix}address-line1`;
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = <InputField label={t2} name={t3} autoComplete={t4} required={true} />;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t(getTranslationKey("cityLabel"));
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `${fieldGroupPrefix}City`;
  const t8 = `${formattedAutoCompletePrefix}address-level2`;
  let t9;
  if ($[10] !== t6 || $[11] !== t7 || $[12] !== t8) {
    t9 = <InputField label={t6} name={t7} autoComplete={t8} required={true} maxLength={LeadFormChurchCityMaxLength} />;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t) {
    t10 = t(getTranslationKey("stateLabel"));
    $[14] = t;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = `${fieldGroupPrefix}State`;
  const t12 = `${formattedAutoCompletePrefix}address-level1`;
  let t13;
  if ($[16] !== t) {
    t13 = t(getTranslationKey("statePlaceholder"));
    $[16] = t;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t13) {
    t14 = <StateField label={t10} name={t11} autoComplete={t12} spellCheck={false} placeholder={t13} items={stateOptions} required={true} />;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t) {
    t15 = t(getTranslationKey("zipCodeLabel"));
    $[23] = t;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = `${fieldGroupPrefix}Postal`;
  const t17 = `${formattedAutoCompletePrefix}postal-code`;
  let t18;
  if ($[25] !== t15 || $[26] !== t16 || $[27] !== t17) {
    t18 = <PostalCodeField label={t15} name={t16} autoComplete={t17} required={true} />;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t14 || $[30] !== t18 || $[31] !== t9) {
    t19 = <FormColumn>{t9}{t14}{t18}</FormColumn>;
    $[29] = t14;
    $[30] = t18;
    $[31] = t9;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t19 || $[34] !== t5) {
    t20 = <>{t5}{t19}</>;
    $[33] = t19;
    $[34] = t5;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  return t20;
};
export const EventForm: React.FC<EventFormProps> = ({
  onSubmit,
  config
}) => {
  const {
    t
  } = useTranslation(['forms', 'skippy']);
  const [formState, setFormState] = useState<EventFormState>({});
  const [{
    isPastor,
    isChildHost,
    hasPermissionToHost,
    acknowledged,
    consent
  }, setQueryState] = useState<FormQueryState>({});
  const defaultCountry = 'US' as CountryCode;
  const audienceSizes: SelectItemProps[] = [{
    value: '',
    text: t('forms:event.audienceSize.placeholder')
  }, ...['1-49', '50-99', '100-199', '200-299', '300-399', '400-499', '500-749', '750-999', '1,000', '2,000', '3,000', '4,000', '5,000', '6,000', '7,000', '8,000', '9,000', '10,000+'].map(value => ({
    value,
    text: value
  }))];
  const roleOptions = [{
    text: t('skippy:form.roleOptions.defaultText'),
    value: ''
  }, ...(config?.roles && config.roles.length > 0 ? config.roles! : [t('skippy:form.roleOptions.pastor'), t('skippy:form.roleOptions.churchStaff'), t('skippy:form.roleOptions.congregant')]).map(makeOption)];
  const handleSubmit = async (data: FieldValues) => {
    let errorMessage = '';
    setFormState(prevState => ({
      ...prevState,
      errorMessage,
      isSubmitting: true
    }));
    const items = (config?.materialOrder ?? []).flatMap((_, index) => {
      return data[`items-${index}`] ?? [];
    });
    const transformedData: LeadEventRequest = {
      ...(data as LeadEventRequest),
      items
    };
    try {
      const success = await onSubmit(transformedData as LeadEventRequest);
      if (success) {
        trackLeadFormSuccess(config?.leadType ?? 'Event Form');
        setFormState({
          showThankYou: true,
          isSubmitting: false
        });
        return;
      }
      errorMessage = t('skippy:form.errorVerify');
    } catch {
      errorMessage = t('skippy:form.errorSave');
    }
    if (errorMessage) {
      setFormState({
        isSubmitting: false,
        errorMessage
      });
    }
  };
  if (formState.showThankYou) {
    const assets = config?.thankYouAssets;
    return <Alert variant="positive" message={config?.messageThankYou ?? t('skippy:form.leadCreate.thankYou')} footer={assets && assets.length > 0 ? <AssetLinksFooter assets={assets} /> : undefined} scrollBehavior="instant" scrollPosition="center" scrollIntoView />;
  }
  return <div className="EventForm max-w-sm">
      {formState.errorMessage && <div className="mb-comfy-xs">
          <Alert className="scroll-mt-roomy-md" variant="negative" message={formState.errorMessage} scrollIntoView />
        </div>}

      <Form className="flex flex-col gap-comfy-sm" onSubmit={handleSubmit}>
        <Fieldset id="contact-fields" header={t('forms:contactFields.contactHeader')}>
          <FormColumn>
            <InputField label={t('forms:contactFields.firstNameLabel')} name="firstName" autoComplete="given-name" required maxLength={LeadFormFirstNameMaxLength} />
            <InputField label={t('forms:contactFields.lastNameLabel')} name="lastName" autoComplete="family-name" required maxLength={LeadFormLastNameMaxLength} />
          </FormColumn>
          <EmailField label={t('forms:contactFields.emailLabel')} name="email" required />
          {!config?.phone?.hide && <PhoneField label={t('forms:contactFields.phoneNumberLabel')} name="phone" countryCode={defaultCountry} required={config?.phone?.required} />}
          {!config?.role?.hide && <Select label={t('forms:contactFields.roleLabel')} name="role" required={config?.role?.required} items={roleOptions} />}
        </Fieldset>
        {config?.hasIdentifierInfo && <Fieldset id="identifier-fields">
            <Text variant={Variants.Label} size={Sizes.Medium}>
              {t('forms:contactFields.identifierInfo')}
            </Text>
            <Checkbox label={t('forms:contactFields.isPastorLabel')} name="isPastor" onChange={({
          currentTarget
        }) => setQueryState(prevState_0 => ({
          ...prevState_0,
          isPastor: currentTarget.checked
        }))} />
            <Checkbox label={t('forms:contactFields.isChildHost')} name="withKids" onChange={({
          currentTarget: currentTarget_0
        }) => setQueryState(prevState_1 => ({
          ...prevState_1,
          isChildHost: currentTarget_0.checked
        }))} />
          </Fieldset>}
        <Fieldset id="shipping-fields" header={t('forms:shippingFields.shippingHeader')}>
          <AddressFields fieldGroupPrefix="shipping" autoCompletePrefix="shipping" />
        </Fieldset>
        <MaterialItems config={config} />
        {config?.hasChurch && <Fieldset id="event-fields" header={t('forms:event.eventHeader')}>
            <DateField label={t('forms:event.eventDateLabel')} name="eventDate" minDaysOut={1} shouldSetInitialDate={false} required />
            <Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Subtle} asChild>
              <p>{t('skippy:form.compassionSunday.churchInfoDisclaimer')}</p>
            </Text>
            <InputField label={t('forms:churchFields.churchNameLabel')} name="churchName" autoComplete="organization" required />
            <AddressFields fieldGroupPrefix="church" autoCompletePrefix="" />

            {!config?.website?.hide && <InputField label={t('forms:churchFields.churchWebsiteLabel')} name="website" required={config?.website?.required} type="url" />}

            <Select label={t('forms:event.audienceSize.label')} name="audienceSize" helpText={t('forms:event.audienceSize.helpText')} items={audienceSizes} required />
          </Fieldset>}
        {(config?.consent || config?.hasIdentifierInfo && (!isPastor || isChildHost)) && <Fieldset className={cn('mt-comfy-xl')}>
            {config?.hasIdentifierInfo && !isPastor && <Checkbox label={t('skippy:form.compassionSunday.permissionLabel')} name="permission" required={!isPastor} onChange={({
          currentTarget: currentTarget_1
        }) => setQueryState(prevState_2 => ({
          ...prevState_2,
          hasPermissionToHost: currentTarget_1.checked
        }))} />}
            {config?.hasIdentifierInfo && isChildHost && <Checkbox label={t('skippy:form.compassionSunday.acknowledgeLabel')} name="acknowledged" required={isChildHost} onChange={({
          currentTarget: currentTarget_2
        }) => setQueryState(prevState_3 => ({
          ...prevState_3,
          acknowledged: currentTarget_2.checked
        }))} />}
            {config?.consent && <Checkbox name="consent" label={config?.consentText ?? t('forms:consentText')} required onChange={({
          currentTarget: currentTarget_3
        }) => setQueryState(prevState_4 => ({
          ...prevState_4,
          consent: currentTarget_3.checked
        }))} />}
          </Fieldset>}

        <FormSubmit className="mt-comfy-xl" isBusy={formState.isSubmitting} disabled={config?.hasIdentifierInfo && !isPastor && !hasPermissionToHost || config?.hasIdentifierInfo && isChildHost && !acknowledged || config?.consent && !consent}>
          {config?.buttonText ?? t('forms:event.buttonText')}
        </FormSubmit>
      </Form>
    </div>;
};
function _temp(option) {
  return {
    value: option.pmId ?? option.id ?? "",
    text: option.label ?? ""
  };
}
function _temp2(options) {
  return options.map(_temp);
}
function _temp3(key) {
  return `forms:addressFields.${key}`;
}